import type { ColorGrade } from './color';
import { defaultColorGrade } from './color';
import type { Keyframe } from './keyframe';

export interface Transform2D {
  positionX: number;
  positionY: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  anchorX: number;
  anchorY: number;
  opacity: number;
}

export type TextAlign = 'left' | 'center' | 'right';

export interface TextStyle {
  content: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
  color: string;
  align: TextAlign;
  letterSpacing: number;
  lineHeight: number;
  strokeColor: string;
  strokeWidth: number;
  shadowColor: string;
  shadowBlur: number;
  shadowOffsetX: number;
  shadowOffsetY: number;
}

export type EffectType = 'none' | 'blur' | 'sharpen' | 'vignette' | 'glow' | 'noise';

export interface Effect {
  id: string;
  type: EffectType;
  enabled: boolean;
  amount: number;
  params: Record<string, number>;
}

/** Per-clip audio processing (volume + insert FX). */
export interface AudioSettings {
  volume: number; // 0..2 linear gain
  noiseRemoval: {
    enabled: boolean;
    /** 0..1 — high-pass / denoise strength */
    amount: number;
  };
  reverb: {
    enabled: boolean;
    /** 0..1 wet mix */
    amount: number;
    /** 0..1 room size / decay */
    size: number;
  };
  eq: {
    enabled: boolean;
    /** dB −12..+12 */
    low: number;
    mid: number;
    high: number;
  };
  compressor: {
    enabled: boolean;
    /** dB −60..0 */
    threshold: number;
    /** 1..20 */
    ratio: number;
    /** seconds */
    attack: number;
    /** seconds */
    release: number;
    /** 0..1 makeup gain */
    makeup: number;
  };
}

export function defaultAudioSettings(): AudioSettings {
  return {
    volume: 1,
    noiseRemoval: { enabled: false, amount: 0.35 },
    reverb: { enabled: false, amount: 0.25, size: 0.45 },
    eq: { enabled: false, low: 0, mid: 0, high: 0 },
    compressor: {
      enabled: false,
      threshold: -24,
      ratio: 4,
      attack: 0.01,
      release: 0.2,
      makeup: 0.2,
    },
  };
}

export type LayerKind = 'video' | 'audio' | 'text' | 'adjustment' | 'solid';

export interface LayerProperties {
  transform: Transform2D;
  color: ColorGrade;
  text?: TextStyle;
  effects: Effect[];
  keyframes: Keyframe[];
  blendMode: 'normal' | 'add' | 'multiply' | 'screen' | 'overlay';
  /** Solid layer fill (#rrggbb) */
  solidColor?: string;
  /** Audio mix / FX (video + audio clips) */
  audio?: AudioSettings;
  /**
   * When true (default), Scale X/Y are locked and shown as one Scale in Xform / Key Graph.
   */
  scaleLinked?: boolean;
}

export function defaultTransform(): Transform2D {
  return {
    positionX: 0,
    positionY: 0,
    scaleX: 1,
    scaleY: 1,
    rotation: 0,
    anchorX: 0.5,
    anchorY: 0.5,
    opacity: 1,
  };
}

export function defaultTextStyle(): TextStyle {
  return {
    content: 'Title',
    fontFamily: 'system-ui',
    fontSize: 72,
    fontWeight: 600,
    color: '#ffffff',
    align: 'center',
    letterSpacing: 0,
    lineHeight: 1.2,
    strokeColor: '#000000',
    strokeWidth: 0,
    shadowColor: 'rgba(0,0,0,0.5)',
    shadowBlur: 8,
    shadowOffsetX: 0,
    shadowOffsetY: 4,
  };
}

export function defaultLayerProperties(): LayerProperties {
  return {
    transform: defaultTransform(),
    color: defaultColorGrade(),
    effects: [],
    keyframes: [],
    blendMode: 'normal',
    audio: defaultAudioSettings(),
    scaleLinked: true,
  };
}

/** Scale X/Y treated as one parameter (default when unset). */
export function isScaleLinked(props: LayerProperties | undefined | null): boolean {
  return props?.scaleLinked !== false;
}

const KF_TIME_EPS = 0.001;

/**
 * Keep scaleY keys in lockstep with scaleX (same times, values, easing).
 * Used when scale is linked in Xform.
 */
export function syncLinkedScaleKeyframes(keyframes: Keyframe[]): Keyframe[] {
  const xs = keyframes
    .filter((k) => k.property === 'scaleX')
    .sort((a, b) => a.time - b.time);
  const rest = keyframes.filter((k) => k.property !== 'scaleX' && k.property !== 'scaleY');
  const oldYs = keyframes.filter((k) => k.property === 'scaleY');

  const ys: Keyframe[] = xs.map((x) => {
    const existing = oldYs.find((y) => Math.abs(y.time - x.time) < KF_TIME_EPS);
    return {
      ...x,
      id: existing?.id ?? crypto.randomUUID(),
      property: 'scaleY',
    };
  });

  return [...rest, ...xs, ...ys];
}
